import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Clock, CopyCheck, GitMerge, UserX } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { DUPLICATE_MATCH_LABELS, type DuplicateFlagView, type DuplicateSide } from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneIcon, toneCardClass } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, formatIsoDate } from '../../../lib/format';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useDuplicateFlag, useDuplicates, useFlagAction } from '../api/candidates-api';
import { StageChip } from '../components/CandidateBits';
import styles from './DuplicatesPage.module.css';

const RESOLVED_LABELS: Readonly<Record<DuplicateFlagView['status'], string>> = {
  open: 'Open',
  confirmed_duplicate: 'Confirmed as the same person',
  not_duplicate: 'Marked as different people',
  merged: 'Merged',
  unmerged: 'Merged, then undone',
};

/**
 * CAND-06 — §L2 tiers 4–6: records that may be one person, side by side (E5 "merge /
 * not a duplicate / decide later"). The likeliest pairs come first; "decide later"
 * sends a pair to the back. N-17 deep-links to one flag, which opens highlighted.
 */
export function DuplicatesPage({ focusFlagId = null }: { readonly focusFlagId?: string | null }): ReactNode {
  const canReview = useCanEdit('candidate');
  const navigate = useNavigate();
  const queue = useDuplicates();
  const focused = useDuplicateFlag(focusFlagId);

  if (!canReview) return <ForbiddenState resource="the duplicate queue" contact="Your admin" />;
  if (queue.isPending) return <div className={styles.page}><SkeletonLines count={8} /></div>;
  if (queue.error) return <ServerErrorState onRetry={() => void queue.refetch()} />;

  const flags = queue.data;
  const isFocusResolved = focusFlagId !== null && focused.data !== undefined && focused.data.status !== 'open';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/candidates" className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          Candidates
        </Link>
        <h1 className={styles.title}>Possible duplicates</h1>
        <p className={styles.meta}>
          People who applied with the same mobile number, or a very similar name with the same birthday, employer or city. Merge them if they are one person.
        </p>
      </header>

      {isFocusResolved && (
        <p className={styles.resolved}>
          The flag you opened is already resolved: <strong>{RESOLVED_LABELS[focused.data.status]}</strong>.
        </p>
      )}

      {flags.length === 0 ? (
        <EmptyState
          title="Nothing to review"
          body="When someone applies who looks like a person already in the system, the pair appears here and the job's recruiter is told."
          action={
            <Button variant="outline" onClick={() => void navigate({ to: '/candidates' })}>
              Back to candidates
            </Button>
          }
        />
      ) : (
        <ul className={styles.flags}>
          {flags.map((flag) => (
            <FlagCard key={flag.id} flag={flag} isFocused={flag.id === focusFlagId} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FlagCard({ flag, isFocused }: { readonly flag: DuplicateFlagView; readonly isFocused: boolean }): ReactNode {
  const action = useFlagAction();
  const show = useToasts((state) => state.show);
  const navigate = useNavigate();
  const ref = useRef<HTMLLIElement>(null);
  const [newer, existing] = flag.candidates;
  const area = useToneArea();

  useEffect(() => {
    if (isFocused) ref.current?.scrollIntoView({ block: 'center' });
  }, [isFocused]);

  async function act(kind: 'dismiss' | 'defer'): Promise<void> {
    try {
      await action.mutateAsync({ flag, action: kind });
      show({ tone: 'success', message: kind === 'dismiss' ? 'Marked as different people' : 'Moved to the end of the queue' });
    } catch (cause) {
      show({ tone: 'danger', message: "The flag didn't change", detail: errorMessage(cause) });
    }
  }

  return (
    <li ref={ref} className={`${styles.flag} ${toneCardClass()} ${isFocused ? styles.focused : ''}`} data-tone={area.tone}>
      <div className={styles.flagHead}>
        <span className={styles.confidence}>
          <ToneIcon tone={area.tone} icon={CopyCheck} />
          {flag.confidence}% likely the same person
        </span>
        <span className={styles.reasons}>
          {flag.matchedOn.map((reason) => (
            <Chip key={reason} tone="neutral" plain>
              {DUPLICATE_MATCH_LABELS[reason] ?? reason}
            </Chip>
          ))}
          {flag.deferredAt && (
            <Chip tone="warning" plain>
              Decide later
            </Chip>
          )}
        </span>
      </div>
      <div className={styles.sides}>
        <Side side={newer} caption="New record" />
        <Side side={existing} caption="Already in the system" />
      </div>
      <div className={styles.actions}>
        <Button variant="ghost" icon={Clock} onClick={() => void act('defer')}>
          Decide later
        </Button>
        <Button variant="outline" icon={UserX} onClick={() => void act('dismiss')}>
          Different people
        </Button>
        <Button
          variant="solid"
          icon={GitMerge}
          onClick={() => void navigate({ to: '/candidates/merge', search: { a: newer.id, b: existing.id, flag: flag.id } })}
        >
          Review and merge
        </Button>
      </div>
    </li>
  );
}

/** The facts beside each record, keyed by the field name §L23 restricts. */
const SIDE_FACTS: readonly { readonly field: keyof DuplicateSide; readonly label: string }[] = [
  { field: 'email', label: 'Email' },
  { field: 'phone', label: 'Mobile' },
  { field: 'city', label: 'City' },
  { field: 'currentEmployer', label: 'Employer' },
  { field: 'dateOfBirth', label: 'Date of birth' },
];

export function Side({ side, caption }: { readonly side: DuplicateSide; readonly caption: string }): ReactNode {
  const restricted = new Set(side.restrictedFields ?? []);
  return (
    <div className={styles.side}>
      <span className={styles.caption}>{caption}</span>
      <Link to="/candidates/$candidateId" params={{ candidateId: side.id }} className={styles.name}>
        <Avatar name={side.fullName} size="sm" />
        {side.fullName}
      </Link>
      <dl className={styles.facts}>
        {SIDE_FACTS.map(({ field, label }) => (
          <div key={field} className={styles.fact}>
            <dt>{label}</dt>
            {/* #5: a field the role may not see reads "—", never left out. */}
            <dd>{restricted.has(field) ? <span title="Not shown to you">—</span> : factValue(side, field)}</dd>
          </div>
        ))}
        <div className={styles.fact}>
          <dt>Added</dt>
          <dd>{formatDate(side.createdAt)}</dd>
        </div>
      </dl>
      <ul className={styles.applications}>
        {side.applications.map((application, index) => (
          <li key={`${application.ventureName}-${index}`} className={styles.application}>
            <span>
              {application.jobTitle ?? 'A job outside your access'} · {application.ventureName}
            </span>
            <StageChip stage={application.stage} />
          </li>
        ))}
        {side.applications.length === 0 && <li>No applications</li>}
      </ul>
    </div>
  );
}

function factValue(side: DuplicateSide, field: keyof DuplicateSide): string {
  if (field === 'dateOfBirth') return side.dateOfBirth ? formatIsoDate(side.dateOfBirth) : '—';
  const value = side[field];
  return typeof value === 'string' && value !== '' ? value : '—';
}
